import userPhotoFallback from "assets/images/user.png";
import {
  formatStaffMemberFullName,
  formatValidQueryParamUrlCommaSeparated
} from "utils/common";

import { createPaginatedEndpoint } from "../utils/services.util";
import { emptySplitApi } from "./empty-split-api";
import { UserRegionAssignment } from "./types/regions.type";
import {
  Group,
  NewStaffMember,
  StaffFilters,
  StaffMember,
  StaffMemberInput,
  StaffResponse
} from "./types/staff.type";

function mapGroupsToRole(groups: Group[]) {
  const GROUP_TO_ROLE: { [key: string]: string } = {
    Audiologists: "Hearing Care Professional",
    "Billing specialists": "Billing specialist",
    "Business owners": "Business owner",
    "Front Office": "Front Office Staff",
    "Patient Care Coordinator": "Patient Care Coordinator"
  };
  const [firstGroup] = groups;
  return GROUP_TO_ROLE[firstGroup?.name] || "";
}

function transformStaffMemberResponse(response: StaffMember): StaffMember {
  const { role, groups, ...memberDetails } =
    formatStaffMemberFullName(response);

  return {
    ...memberDetails,
    groups,
    avatar: memberDetails.photo ?? userPhotoFallback,
    role: role || mapGroupsToRole(groups)
  };
}

export type DocumentSignerArgs = {
  id: number;
  licenseClinicId?: number;
  licenseAppointmentId?: number;
};

export function documentSignerUrl({
  id,
  licenseClinicId,
  licenseAppointmentId
}: DocumentSignerArgs) {
  return `/staff/${id}/?${formatValidQueryParamUrlCommaSeparated({
    license_clinic: licenseClinicId,
    license_appointment: licenseAppointmentId
  })}`;
}

const getClinicsFilter = (params: StaffFilters) => {
  if ("clinics" in params) {
    return { clinics: params.clinics?.join(",") ?? "" };
  }

  if ("clinicIds" in params) {
    return { clinics: params.clinicIds?.join(",") ?? "" };
  }

  return undefined;
};

const STAFF_QUERY =
  "{id,touchpoint_notifications_enabled,scheduler_select_all_staff,scheduler_persist_per_clinic,last_name,non_npi_id_qualifier,npi,role,patient_arrived_sound_enabled,user_preferences,first_name,task_is_assigned_notifications_enabled,signature,photo,title,suffix,license_number,user_reminder_notifications_enabled,suno_comms_id,date_joined,payment_request_notifications_enabled,is_active,fax_phone,full_name,last_login,user_permissions,non_npi_id,onboarding_form_notifications_enabled,name,color,noah_username,groups{id,name,permissions},email,clinics{id,name,timezone,noah_provider,noah_alias,noah_tenant_id},sendbird_chat_enabled,web_scheduler_enabled,can_see_manufacturer_cost,access_to_costs,has_access_to_admin_panel,able_to_delete_invoices,allow_tilled_refund,show_own_clinics_only,has_access_to_collections,has_access_to_dashboards,has_access_to_qbo,can_view_and_create_campaigns,can_view_catalog,can_edit_catalog,has_access_to_google_reviews,has_api_key}";
// Admin-panel inactive tab (SN-19777): the backend computes `future_appointments_count`
// (a per-row COUNT of unconfirmed/confirmed appointments from today on) only when the
// RestQL query names it, so it is opted into here rather than added to STAFF_QUERY.
export const STAFF_QUERY_WITH_FUTURE_APPOINTMENTS = STAFF_QUERY.replace(
  /}$/,
  ",future_appointments_count}"
);
// The Voice seat (ENG-2414) for the Users list's Voice chip, asked for only on
// a Voice practice so every other practice's request stays as it was. Backend
// first: an older API answers an unknown RestQL key with a 400 for the list.
export const withVoiceEnabledField = (query: string, enabled: boolean) =>
  enabled ? query.replace(/}$/, ",voice_enabled}") : query;
const STAFF_QUERY_WITH_TZ =
  "{id,touchpoint_notifications_enabled,scheduler_select_all_staff,scheduler_persist_per_clinic,last_name,non_npi_id_qualifier,npi,role,patient_arrived_sound_enabled,user_preferences,first_name,task_is_assigned_notifications_enabled,signature,photo,title,suffix,license_number,user_reminder_notifications_enabled,suno_comms_id,date_joined,payment_request_notifications_enabled,is_active,fax_phone,full_name,last_login,user_permissions,non_npi_id,onboarding_form_notifications_enabled,name,color,noah_username,groups{id,name,permissions},email,clinics{id,name,timezone,noah_provider,noah_alias,noah_tenant_id},default_clinic{id,name,timezone},can_see_manufacturer_cost,access_to_costs,has_access_to_admin_panel,has_access_to_dashboards,has_access_to_qbo,can_view_and_create_campaigns,can_view_catalog,can_edit_catalog,has_access_to_google_reviews,has_api_key}";

export interface ClinicLicenseDefault {
  clinic_id: number;
  clinic_name: string;
  default_license_field: "license_number" | "additional_license_number" | null;
  default_state_license?: string | null;
}

export interface ClinicLicenseDefaultInput {
  clinic_id: number;
  default_license_field?: "license_number" | "additional_license_number" | null;
  default_state_license?: string | null;
}

export interface ClinicLicenseDefaultsUpdateResponse {
  data: ClinicLicenseDefault[];
  invalid_clinic_ids: number[];
}

// ISO weekdays (1 = Monday .. 7 = Sunday); null = every working day is bookable online.
export interface WebSchedulingDays {
  clinic_id: number;
  clinic_name: string;
  web_scheduling_weekdays: number[] | null;
}

export interface WebSchedulingDaysInput {
  clinic_id: number;
  web_scheduling_weekdays: number[] | null;
}

export interface WebSchedulingDaysUpdateResponse {
  data: WebSchedulingDays[];
  invalid_clinic_ids: number[];
}

export interface SyncSendbirdPhotoResponse {
  status: "ok";
  profile_url: string | null;
}

export interface GenerateApiKeyResponse {
  api_key: string;
  name: string;
}

export const staffApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getStaffPaginated: createPaginatedEndpoint<
      StaffFilters & { limit?: number; offset?: number },
      StaffResponse
    >(builder, {
      queryFn: (params) => {
        const {
          groupName,
          is_active = true,
          limit = 25,
          offset = 0,
          query = STAFF_QUERY,
          search
        } = params;

        return {
          url: `/staff/?${formatValidQueryParamUrlCommaSeparated({
            ...getClinicsFilter(params),
            groups__name: groupName ?? "",
            is_active: is_active === null ? "" : String(is_active),
            limit,
            offset,
            query: query,
            search: search ? encodeURIComponent(search) : undefined
          })}`
        };
      },
      providesTags: (result: StaffResponse[]) => {
        return Object.entries(result || []).reduce(
          (acc, [key, value]) => {
            if (!isNaN(Number(key))) {
              value.forEach((staff) => {
                acc.push({
                  type: "Staff" as const,
                  id: staff.id
                });
              });
            }

            return acc;
          },
          [] as { type: "Staff"; id: number }[]
        );
      },
      transformResponseFn: (response: StaffResponse) => {
        return response
          .map(formatStaffMemberFullName)
          .map(({ role, groups, ...memberDetails }: StaffMember) => ({
            ...memberDetails,
            groups,
            avatar: memberDetails.photo ?? userPhotoFallback,
            role: role || mapGroupsToRole(groups)
          }));
      }
    }),
    getStaff: builder.query<StaffResponse, StaffFilters>({
      query: (params) => {
        const {
          groupName,
          is_active = true,
          web_scheduler_enabled,
          query = STAFF_QUERY_WITH_TZ,
          search
        } = params;

        return {
          url: `/staff/?${formatValidQueryParamUrlCommaSeparated({
            ...getClinicsFilter(params),
            search: search && encodeURIComponent(search),
            groups__name: groupName ?? "",
            is_active: is_active === null ? "" : String(is_active),
            web_scheduler_enabled:
              web_scheduler_enabled == null
                ? ""
                : String(web_scheduler_enabled),
            limit: 300,
            query: query
          })}`
        };
      },

      providesTags: (result) =>
        result?.map((staff) => ({
          type: "Staff" as const,
          id: staff.id
        })) ?? [],
      transformResponse: (response: StaffResponse) =>
        response
          .map(formatStaffMemberFullName)
          .map(({ role, groups, ...memberDetails }: StaffMember) => ({
            ...memberDetails,
            groups,
            avatar: memberDetails.photo ?? userPhotoFallback,
            role: role || mapGroupsToRole(groups)
          }))
    }),
    queryStaffCounter: builder.query<number, Omit<StaffFilters, "query">>({
      query: (filters) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...filters
        });

        return `/staff/count/?${customFiltersString}`;
      },
      providesTags: ["Staff"]
    }),
    getStaffMember: builder.query<StaffMember, number>({
      query: (id: number) => `/staff/${id}/`,
      providesTags: (result) => [
        {
          type: "Staff" as const,
          id: result?.id
        }
      ],
      transformResponse: transformStaffMemberResponse
    }),
    // A document's signer: the clinic (and visit) let the backend resolve which state license
    // `resolved_license_number` is for. Without them it is null and the legacy license prints.
    getDocumentSigner: builder.query<StaffMember, DocumentSignerArgs>({
      query: documentSignerUrl,
      providesTags: (result) => [
        {
          type: "Staff" as const,
          id: result?.id
        }
      ],
      transformResponse: transformStaffMemberResponse
    }),
    getAllStaff: builder.query<
      StaffResponse,
      { search?: string; query?: string; limit?: number }
    >({
      query: ({ query = STAFF_QUERY, limit = 300, search }) => ({
        url: `/staff/?${formatValidQueryParamUrlCommaSeparated({ limit, query, search: search && encodeURIComponent(search) })}`
      }),
      providesTags: ["Staff"]
    }),
    deleteStaffMember: builder.mutation<StaffMember, { id: number }>({
      query: ({ id }: { id: number }) => ({
        url: `/staff/${id}/`,
        method: "PATCH",
        body: {
          is_active: false
        }
      }),
      invalidatesTags: ["Staff"]
    }),
    getGroups: builder.query<Pick<Group, "id" | "name">[], void>({
      query: () => `/groups/?limit=300&ordering=name&query={id,name}`,
      transformResponse: (response: {
        results: Pick<Group, "id" | "name">[];
      }) => response.results
    }),
    createStaffMember: builder.mutation<StaffMember, NewStaffMember>({
      query: (body) => {
        return {
          url: `/staff/`,
          method: "POST",
          body: body
        };
      },
      invalidatesTags: ["Staff"]
    }),
    updateStaffMember: builder.mutation<StaffMember, StaffMemberInput>({
      query: ({ id, ...body }) => ({
        url: `/staff/${id}/`,
        method: "PATCH",
        body: { ...body }
      }),
      invalidatesTags: ["Staff"]
    }),
    changeStaffMemberPassword: builder.mutation<
      StaffMember,
      { id: number; password1: string; password2: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/staff/${id}/set-password/`,
        method: "PATCH",
        body: body
      })
    }),
    syncStaffMemberSendbirdPhoto: builder.mutation<
      SyncSendbirdPhotoResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/staff/${id}/sync-sendbird-photo/`,
        method: "PATCH"
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Staff", id },
        "SendbirdUsers"
      ]
    }),
    generateStaffMemberApiKey: builder.mutation<
      GenerateApiKeyResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/staff/${id}/generate-api-key/`,
        method: "POST"
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Staff", id }]
    }),
    getStaffRegions: builder.query<UserRegionAssignment[], number>({
      query: (id) => `/staff/${id}/regions/`,
      providesTags: (_result, _error, id) => [{ type: "StaffRegions", id }]
    }),
    updateStaffRegions: builder.mutation<
      UserRegionAssignment[],
      { id: number; assignments: Omit<UserRegionAssignment, "region_name">[] }
    >({
      query: ({ id, assignments }) => ({
        url: `/staff/${id}/regions/`,
        method: "PATCH",
        body: { assignments }
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "StaffRegions", id }
      ]
    }),
    getClinicLicenseDefaults: builder.query<ClinicLicenseDefault[], number>({
      query: (id) => `/staff/${id}/clinic-license-defaults/`,
      providesTags: (_result, _error, id) => [
        { type: "StaffLicenseDefaults", id }
      ]
    }),
    updateClinicLicenseDefaults: builder.mutation<
      ClinicLicenseDefaultsUpdateResponse,
      { id: number; defaults: ClinicLicenseDefaultInput[] }
    >({
      query: ({ id, defaults }) => ({
        url: `/staff/${id}/clinic-license-defaults/`,
        method: "PATCH",
        body: defaults
      }),
      // Write the saved rows from the response straight into the cache, rather
      // than invalidating: during an invalidation refetch the query hook keeps
      // rendering its previous result, so the select would show the old value
      // until the refetch landed. Only the rows this request changed are
      // written — the response lists every clinic as the server read them when
      // the request started, so an overlapping save would otherwise put a
      // just-picked clinic back to its old value on screen.
      async onQueryStarted({ id, defaults }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const changed = new Set(defaults.map((d) => d.clinic_id));
          dispatch(
            staffApi.util.updateQueryData(
              "getClinicLicenseDefaults",
              id,
              (draft) => {
                data.data
                  .filter((row) => changed.has(row.clinic_id))
                  .forEach((row) => {
                    const index = draft.findIndex(
                      (d) => d.clinic_id === row.clinic_id
                    );
                    if (index >= 0) draft[index] = row;
                  });
              }
            )
          );
        } catch {
          // The caller unwraps the mutation and reports the failure.
        }
      }
    }),
    getWebSchedulingDays: builder.query<WebSchedulingDays[], number>({
      query: (id) => `/staff/${id}/web-scheduling-days/`,
      providesTags: (_result, _error, id) => [
        { type: "StaffWebSchedulingDays", id }
      ]
    }),
    updateWebSchedulingDays: builder.mutation<
      WebSchedulingDaysUpdateResponse,
      { id: number; days: WebSchedulingDaysInput[] }
    >({
      query: ({ id, days }) => ({
        url: `/staff/${id}/web-scheduling-days/`,
        method: "PATCH",
        body: days
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "StaffWebSchedulingDays", id }
      ]
    })
  })
});

export const {
  useGetStaffQuery,
  useLazyGetStaffQuery,
  useGetStaffMemberQuery,
  useGetDocumentSignerQuery,
  useGetAllStaffQuery,
  useGetGroupsQuery,
  useDeleteStaffMemberMutation,
  useUpdateStaffMemberMutation,
  useCreateStaffMemberMutation,
  useChangeStaffMemberPasswordMutation,
  useSyncStaffMemberSendbirdPhotoMutation,
  useGenerateStaffMemberApiKeyMutation,
  useQueryStaffCounterQuery,
  useGetStaffRegionsQuery,
  useUpdateStaffRegionsMutation,
  useGetClinicLicenseDefaultsQuery,
  useUpdateClinicLicenseDefaultsMutation,
  useGetWebSchedulingDaysQuery,
  useUpdateWebSchedulingDaysMutation
} = staffApi;
